// third-party
import { createSlice } from '@reduxjs/toolkit';


// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    rooms: [],
    roomsPrices: [],
    degressives: [],
    roomsCalculatedPrices: [],
    room: {},
    listings: [],
    fees: [],
    otas: [],
    owners: [],
    notes: [],
    tags: [],
    standings: [],
    cleaningClusters: [],
    skills: [],
    availableSkills: [],
    roomSkills: [],
    skillsHistory: [],
};

const slice = createSlice({
    name: 'roomsControl',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        // GET Planning
        getRoomsSuccess(state, action) {
            state.rooms = action.payload;
        },
        getRoomsPricesSuccess(state, action) {
            state.roomsPrices = action.payload;
        },
        getDegressivesSuccess(state, action) {
            state.degressives = action.payload;
        },
        getRoomsCalculatedPricesSuccess(state, action) {
            state.roomsCalculatedPrices = action.payload;
        },
        getOtasSuccess(state, action) {
            state.otas = action.payload;
        },

        getRoomSuccess(state, action) {
            state.room = action.payload;
        },

        getRoomDetailsSuccess(state, action) {
            state.roomDetails = action.payload;
        },

        getListingsSuccess(state, action) {
            state.listings = action.payload;
        },

        getOwnersSuccess(state, action) {
            state.owners = action.payload
        },

        getFeesSuccess(state, action) {
            state.fees = action.payload
        },

        getNotesSuccess(state, action) {
            state.notes = action.payload
        },
        getTagsSuccess(state, action) {
            state.tags = action.payload
        },
        getStandingsSuccess(state, action) {
            state.standings = action.payload
        },
        getCleaningClustersSuccess(state, action) {
            state.cleaningClusters = action.payload
        },
        // Skills reducers
        getAvailableSkillsSuccess(state, action) {
            state.availableSkills = action.payload
        },
        getRoomSkillsSuccess(state, action) {
            state.roomSkills = action.payload
        },
        getSkillsHistorySuccess(state, action) {
            state.skillsHistory = action.payload
        },
        updateRoomSkillSuccess(state, action) {
            // Find and update the skill in the roomSkills array
            const index = state.roomSkills.findIndex(s => s.skill_id === action.payload.skill_id);
            if (index !== -1) {
                state.roomSkills[index] = action.payload;
            }
        },
        addRoomSkillSuccess(state, action) {
            state.roomSkills.push(action.payload);
        },
        removeRoomSkillSuccess(state, action) {
            state.roomSkills = state.roomSkills.filter(s => s.skill_id !== action.payload);
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getRooms(search, showRemoved) {
    return async () => {
        try {
            let queryParams = new URLSearchParams();
            if(search) queryParams.append('search', search);
            if(showRemoved) queryParams.append('show_removed', showRemoved);
            const response = await axios.get(`/api/rooms${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
            dispatch(slice.actions.getRoomsSuccess(response.data.rooms));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const getTags = () => async () => {
    try {
        const response = await axios.get(`/api/rooms/tags`);
        dispatch(slice.actions.getTagsSuccess(response.data.tags));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const getStandings = () => async () => {
    try {
        const response = await axios.get(`/api/rooms/watchlist/standings`);
        dispatch(slice.actions.getStandingsSuccess(response.data.standings));
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export function getOtas() {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/otas`);
            dispatch(slice.actions.getOtasSuccess(response.data.otas));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getRoom(id) {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/${id}`);
            dispatch(slice.actions.getRoomSuccess(response.data.room));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getRoomDetails(id) {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/${id}/amenities`);
            dispatch(slice.actions.getRoomDetailsSuccess(response.data.listing));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function resetRoom() {
    return async () => {
        try {
            dispatch(slice.actions.getRoomSuccess({}));
            dispatch(slice.actions.hasError(null));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeRoom(id) {
    return async () => {
        try {
            const response = await axios.delete(`/api/rooms/${id}`);
            if(response.data.success) {
                dispatch(slice.actions.getRoomSuccess(response.data.room));
            } else {
                dispatch(slice.actions.hasError(response.data.message));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function reactivateRoom(id) {
    return async () => {
        try {
            const response = await axios.post(`/api/rooms/${id}/reactivate`);
            if(response.data.success) {
                dispatch(slice.actions.getRoomSuccess(response.data.room));
            } else {
                dispatch(slice.actions.hasError(response.data.message));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const updateRoom = (id, data) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${id}`, data);
        if(response.data.success) {
            dispatch(slice.actions.getRoomSuccess(response.data.room));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const duplicateRoom = (id) => async () => {
    try {
        const response = await axios.post(`/api/rooms/${id}/duplicate`);
        if(response.data.success) {
            return response.data.room;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const updateRoomAddress = (id, data) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${id}/address`, data);
        if(response.data.success) {
            dispatch(slice.actions.getRoomSuccess(response.data.room));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export function getCleaningClusters() {
    return async (dispatch) => {
        try {
            const response = await axios.get('/api/rooms/cleaning-clusters');
            if(response.data.success) {
                dispatch(slice.actions.getCleaningClustersSuccess(response.data.clusters));
            } else {
                dispatch(slice.actions.hasError(response.data.message));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const updateRoomFees = (id, data) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${id}/fees`, data);
        if(response.data.success) {
            dispatch(slice.actions.getRoomSuccess(response.data.room));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const updateRoomInvoices = (id, data) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${id}/invoices`, data);
        if(response.data.success) {
            dispatch(slice.actions.getRoomSuccess(response.data.room));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export function getOwners() {
    return async () => {
        try {
            const response = await axios.get(`/api/users/OWNERS?limit=1000`);
            dispatch(slice.actions.getOwnersSuccess(response.data.users));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getListings() {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/listings`);
            dispatch(slice.actions.getListingsSuccess(response.data.listings));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getFees() {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/fees`);
            dispatch(slice.actions.getFeesSuccess(response.data.fees));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateListing(data) {
    return async () => {
        try {
            const response = await axios.post(`/api/rooms/listings`, data);
            dispatch(slice.actions.getListingsSuccess(response.data.listings));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getRoomNotes(id) {
    return async () => {
        try {
            const response = await axios.get(`/api/rooms/${id}/notes`);
            dispatch(slice.actions.getNotesSuccess(response.data.notes));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function addRoomNote(id, data) {
    return async () => {
        try {
            const response = await axios.put(`/api/rooms/${id}/notes`, {note: data});
            dispatch(slice.actions.getNotesSuccess(response.data.notes));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeRoomNote(id, noteId) {
    return async () => {
        try {
            const response = await axios.delete(`/api/rooms/${id}/notes/${noteId}`);
            dispatch(slice.actions.getNotesSuccess(response.data.notes));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function editRoomNote(id, noteId, data) {
    return async () => {
        try {
            const response = await axios.post(`/api/rooms/${id}/notes/${noteId}`, {note: data});
            dispatch(slice.actions.getNotesSuccess(response.data.notes));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export const getIntructionsTranslations = (id, lang, force) => async () => {
    try {
        const response = await axios.get(`/api/rooms/${id}/instructions/${lang}${force ? '?force=true' : ''}`);
        return response.data.instructions;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const updateTranslation = (id, lang, translation) => async () => {
    try {
        const response = await axios.post(`/api/rooms/${id}/instructions/${lang}`, {translation});
        return response.data.instructions;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const updateInstructions = (id, instructions) => async () => {
    try {
        const response = await axios.post(`/api/rooms/${id}/instructions`, {instructions});
        return response.data.instructions;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const updateGptInstructions = (id, instructions) => async () => {
    try {
        const response = await axios.post(`/api/rooms/${id}/instructions/gpt`, {instructions});
        return response.data.instructions;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const getRoomsPrices = (rooms) => async () => {
    try {
        const response = await axios.get(`/api/rooms/prices/get?rooms=${rooms.join(',')}`);
        if(response.data.success) {
            dispatch(slice.actions.getRoomsPricesSuccess(response.data.prices));
            // dispatch(slice.actions.getDegressivesSuccess(response.data.degressives));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
        return response.data.prices;
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const getDegressives = () => async () => {
    try {
        const response = await axios.get(`/api/rooms/prices/degressives`);
        if(response.data.success) {
            dispatch(slice.actions.getDegressivesSuccess(response.data.degressives));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const setDegressives = (key, degressives) => async () => {
    try {
        const response = await axios.post(`/api/rooms/prices/degressives`, {key, degressives});
        if(response.data.success) {
            return dispatch(slice.actions.getDegressivesSuccess(response.data.degressives));
        } else {
            return dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        return dispatch(slice.actions.hasError(error));
    }
}

export const setRoomsPrices = (prices) => async () => {
    try {
        const response = await axios.post(`/api/rooms/prices/set`, {prices});
        if(response.data.success) {
            dispatch(slice.actions.getRoomsPricesSuccess(response.data.prices));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const getRoomsCalculatedPrices = (rooms, period) => async () => {
    try {
        const response = await axios.get(`/api/rooms/prices/calculated?rooms=${rooms.join(',')}&period=${period}`);
        if(response.data.success) {
            dispatch(slice.actions.getRoomsCalculatedPricesSuccess(response.data.calculatedPrices));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
}

export const syncRoomsPrices = (rooms) => async () => {
    try {
        const response = await axios.get(`/api/hook/hostify/prices/sync?rooms=${rooms.join(',')}`);
        if(response.data.success) {
            return true;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return false;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return false;
    }
}

// ============================================
// SKILLS ACTIONS
// ============================================

/**
 * Get all available skills with categories
 */
export const getAvailableSkills = () => async () => {
    try {
        const response = await axios.get('/api/skills');
        if (response.data.success) {
            dispatch(slice.actions.getAvailableSkillsSuccess(response.data.skills));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
};

/**
 * Get skills for a specific room
 */
export const getRoomSkills = (roomId) => async () => {
    try {
        const response = await axios.get(`/api/rooms/${roomId}/skills`);
        if (response.data.success) {
            dispatch(slice.actions.getRoomSkillsSuccess(response.data.skills));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
};

/**
 * Get history of skill changes for a room
 */
export const getSkillsHistory = (roomId, skillId = null) => async () => {
    try {
        const url = skillId
            ? `/api/rooms/${roomId}/skills/history?skill_id=${skillId}`
            : `/api/rooms/${roomId}/skills/history`;
        const response = await axios.get(url);
        if (response.data.success) {
            dispatch(slice.actions.getSkillsHistorySuccess(response.data.history));
        } else {
            dispatch(slice.actions.hasError(response.data.message));
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
    }
};

/**
 * Update a room skill
 */
export const updateRoomSkill = (roomId, skillId, details) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${roomId}/skills/${skillId}`, { details });
        if (response.data.success) {
            dispatch(slice.actions.updateRoomSkillSuccess(response.data.skill));
            // Refresh room skills
            dispatch(getRoomSkills(roomId));
            return true;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return false;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return false;
    }
};

/**
 * Batch update multiple room skills
 */
export const updateRoomSkills = (roomId, skills) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${roomId}/skills`, { skills });
        if (response.data.success) {
            // Refresh room skills after batch update
            dispatch(getRoomSkills(roomId));
            return true;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return false;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return false;
    }
};

/**
 * Add a new skill to a room
 */
export const addRoomSkill = (roomId, skillId, details) => async () => {
    try {
        const response = await axios.put(`/api/rooms/${roomId}/skills/${skillId}`, { details });
        if (response.data.success) {
            dispatch(slice.actions.addRoomSkillSuccess(response.data.skill));
            // Refresh room skills
            dispatch(getRoomSkills(roomId));
            return true;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return false;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return false;
    }
};

/**
 * Remove a skill from a room
 */
export const removeRoomSkill = (roomId, skillId) => async () => {
    try {
        const response = await axios.delete(`/api/rooms/${roomId}/skills/${skillId}`);
        if (response.data.success) {
            dispatch(slice.actions.removeRoomSkillSuccess(skillId));
            // Refresh room skills
            dispatch(getRoomSkills(roomId));
            return true;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return false;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return false;
    }
};

/**
 * Create a new skill (admin only)
 */
export const createSkill = (name, category) => async () => {
    try {
        const response = await axios.post('/api/skills', { name, category });
        if (response.data.success) {
            // Refresh available skills
            dispatch(getAvailableSkills());
            return response.data.skill;
        } else {
            dispatch(slice.actions.hasError(response.data.message));
            return null;
        }
    } catch (error) {
        dispatch(slice.actions.hasError(error));
        return null;
    }
};